import React, { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/pagination';
import { getApiListing, getApiListingsByIdRelated, Listing as ListingAPIData, patchApiListingsById, PatchApiListingsByIdData, RoomType, UpdateListing } from '../../../api/listings-client';
import { useAuth } from '../../../AuthContext';
import apartment from '../../../resources/apartment.webp';
import messageSvg from '../../../resources/message.svg';
import { capitalizeFirstLetters } from '../../../util/AddressUtil';
import Footer from '../../Footer';
import MainHeader from '../../MainHeader';
import { ListingsSwiperView } from '../../ListingsSwiperView';
import ToastError from '../CreateListing/ToastError';
import './custom-slider.css';
import DetailsSection from './DetailsSection';
import EditButton, { EditIcon } from './EditButton';
import EditModal from './EditModal';
import { EditListingChanges } from './EditTypes';
import FloatingMessageBox from './FloatingMessageBox';
import ImageCarousel from './ImageCarousel';
import ModalPrompt from './ModalPrompt';
import PMTilesMap from './PMTilesMap';
import ListingSkeleton from './ListingSkeleton';
import NotFound from '../../NotFound';
import { FileX } from 'lucide-react';

const ListingNotFound = ({ isOwner = false }) => {
  if (isOwner) {
    return (
      <NotFound
        title="Listing Pending Approval"
        message="Your listing is currently under review and not yet visible to other users. You'll be notified once it's approved."
        icon={<FileX className="w-12 h-12 text-white" />}
      />
    );
  }

  return (
    <NotFound
      title="Listing Not Available"
      message="This listing isn't available or may have been removed."
      icon={<FileX className="w-12 h-12 text-white" />}
    />
  );
};

// Prepare images for the carousel, optionally using the first image's small version if coming from a listing card
function prepareCarouselImages(localListing: ListingAPIData, cameFromListingCard = false): { large: string; small?: string }[] {
  if (localListing.images && localListing.images.length > 0) {
    return localListing.images.map((image, index) => {
      // For large images
      let large = image.large;
      if (large && !/^(https?:\/\/|data:)/i.test(large.trim())) {
        large = `https://${large}`;
      }

      // Only include small image for the first image, and only if we came from a listing card
      let small = undefined;
      if (index === 0 && cameFromListingCard && image.small) {
        small = image.small;
        if (!/^(https?:\/\/|data:)/i.test(small.trim())) {
          small = `https://${small}`;
        }
      }

      return {
        large: large || apartment,
        small: small
      };
    });
  }
  return [{ large: apartment }];
}

function formatDescription(description: string) {
  return description.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ));
};

interface OtherListingsSectionProps {
  localListing: ListingAPIData;
}

const OtherListingsSection: React.FC<OtherListingsSectionProps> = ({ localListing }) => {
  const [relatedListings, setRelatedListings] = useState<ListingAPIData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelatedListings = async () => {
      if (!localListing.id) return;

      try {
        setIsLoading(true);
        const listings = await getApiListingsByIdRelated({
          id: localListing.id
        });
        setRelatedListings(listings.filter(listing => listing.id !== localListing.id));
      } catch (err) {
        console.error('Failed to fetch related listings:', err);
        setError('Failed to load related listings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedListings();
  }, [localListing.id]);

  // Don't show section if there are no related listings and we're not loading
  if (!isLoading && relatedListings.length === 0) {
    return null;
  }

  // Don't show section if there was an error
  if (error) {
    return null;
  }

  return (
    <div>
      <div className='pt-6 pb-12'>
        <p className="text-xl font-medium pb-6 pl-2 md:pl-0">
          More listings near {capitalizeFirstLetters(localListing.suburb)}
        </p>

        <ListingsSwiperView
          listings={relatedListings}
          isLoading={isLoading}
          showViewAll={false}
        />
      </div>
    </div>
  );
};

interface MobileScrollButtonProps {
  messageBoxRef: RefObject<HTMLDivElement>;
}

const MobileScrollButton: React.FC<MobileScrollButtonProps> = ({ messageBoxRef }) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const scrollStartY = useRef<number | null>(null);
  const hasScrolled = useRef(false);

  const scrollToMessageBox = () => {
    if (messageBoxRef.current) {
      const yOffset = -100;
      const elementY = messageBoxRef.current.getBoundingClientRect().top + window.pageYOffset;
      const targetY = elementY + yOffset;
      const startY = window.pageYOffset;
      const difference = targetY - startY;
      const duration = 700;
      let start: number;

      const step = (timestamp: number) => {
        if (!start) start = timestamp;
        const time = timestamp - start;
        const percent = Math.min(time / duration, 1);
        const easeInOutCubic = percent < 0.5
          ? 4 * percent * percent * percent
          : 1 - Math.pow(-2 * percent + 2, 3) / 2;

        window.scrollTo({
          top: startY + difference * easeInOutCubic,
          left: 0,
          behavior: 'instant'
        });
        if (time < duration) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    }
  };

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      scrollStartY.current = window.scrollY;
      hasScrolled.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current !== null && scrollStartY.current !== null) {
        const currentScrollY = window.scrollY;

        // Detect any vertical scroll
        if (currentScrollY !== scrollStartY.current) {
          setIsScrolling(true);
          hasScrolled.current = true;
        }
      }
    };

    const handleTouchEnd = () => {
      if (hasScrolled.current) {
        setIsScrolling(false);
      }
      touchStartY.current = null;
      scrollStartY.current = null;
      hasScrolled.current = false;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div className="fixed bottom-4 left-0 right-0 flex justify-center z-50 lg:hidden">
      <button
        onClick={scrollToMessageBox}
        className={`py-3 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-full border border-transparent text-white bg-gradient-to-r from-[#24ccf2] to-[#2476f2] disabled:pointer-events-none shadow-lg transition-opacity duration-300 ${isScrolling ? 'opacity-30' : 'opacity-100'
          }`}
        aria-label="Scroll to message box"
      >
        <img
          src={messageSvg}
          className='flex-shrink-0 size-6 md:size-7 text-white'
          alt="Message icon"
        />
        Message Owner
      </button>
    </div>
  );
};

interface ListingWrapProps {
}

const ListingWrap: FC<ListingWrapProps> = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const mapRef = useRef<HTMLDivElement>(null);

  const [localListing, setLocalListing] = useState<ListingAPIData | null>(
    (location.state?.listing && location.state.listing.id === id)
      ? location.state.listing
      : null
  );
  const [editMode, setEditMode] = useState<boolean>(location.state?.listing && location.state?.listing.owner?.id === user?.userId);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(
    !(location.state?.listing && location.state.listing.id === id)
  );
  const [notFound, setNotFound] = useState<boolean>(false);
  const messageBoxRef = useRef<HTMLDivElement>(null);

  // Determine if we navigated directly from a listing card with this listing
  const cameFromListingCard = Boolean(location.state?.listing && location.state.listing.id === id);

  useEffect(() => {
    // Check if listing was passed via location state
    if (location.state?.listing && location.state.listing.id === id) {
      setLocalListing(location.state.listing);
      setIsLoading(false);
      if (location.state.listing.owner?.id === user?.userId) {
        setEditMode(true);
      }
    } else {
      // Fetch from API if not passed or if IDs don't match
      async function fetchListing() {
        try {
          const listingData = await getApiListing({ id });
          setLocalListing(listingData);
          if (listingData.owner?.id === user?.userId) {
            setEditMode(true);
          }
        } catch (error) {
          console.error("Error fetching listing:", error);
          // Check for 404 error
          // The ApiError from your client may not have a response property
          // Look for status code, message, or the error message string
          if (error.status === 404) {
            setNotFound(true);
          } else {
            setError("Failed to load listing");
          }
        } finally {
          setIsLoading(false);
        }
      }
      fetchListing();
    }
  }, [id, location.state, user?.userId]);

  useEffect(() => {
    if (localListing) {
      // Determine room type text
      const roomTypeText = localListing.roomType === RoomType.SHARED ? "Shared" : "Private";

      // Format location
      const location = `${capitalizeFirstLetters(localListing.suburb)} ${localListing.state} ${localListing.postcode}`;

      // Format features
      const rentText = `$${localListing.rent} / week`;
      const furnishedText = localListing.furnished ? "furnished" : "unfurnished";
      const billsText = localListing.billsIncluded ? ", bills included" : "";
      const parkingText = localListing.parking ? ", with parking" : "";

      // Set the document title with improved readability
      document.title = `${roomTypeText} room for rent in ${location} (${rentText}, ${furnishedText}${billsText}${parkingText})`;
    } else if (notFound) {
      document.title = "Propyn - Listing Not Found";
    }
  }, [localListing, notFound]);

  const initialImages = useMemo(() => {
    if (!localListing || !localListing.images) return [];

    return localListing.images.map((image, index) => ({
      id: image.mediaId || `image-${index}`,
      mediaId: image.mediaId,
      image: image.large
        ? image.large.startsWith('http://') || image.large.startsWith('https://') || image.large.startsWith('data:')
          ? image.large
          : `https://${image.large}`
        : "",
    }));
  }, [localListing]); // Only recalculate if localListing changes


  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentModalPrompt, setCurrentModalPrompt] = useState<ModalPrompt>(ModalPrompt.Description);
  const [initialDetailsTab, setInitialDetailsTab] = useState<'Facilities' | 'Pricing' | 'Time'>('Facilities');

  const handleModalOpen = (modalPrompt: ModalPrompt, initialTab?: 'Facilities' | 'Pricing' | 'Time') => {
    setCurrentModalPrompt(modalPrompt);
    setInitialDetailsTab(initialTab || 'Facilities');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const updateLocalListing = (
    prevListing: ListingAPIData | null,
    changes: EditListingChanges
  ): ListingAPIData | null => {
    if (!prevListing) return null;

    const { images, ...otherChanges } = changes;

    // Process images if they exist in the changes
    const updatedImages = images
      ? images.map(image => ({
        mediaId: image.mediaId,
        small: image.image,
        large: image.image,
      }))
      : prevListing.images;

    return {
      ...prevListing,
      ...otherChanges,
      images: updatedImages,
    };
  };

  const handleSubmit = async (changes: EditListingChanges) => {
    // Handle the submission of changes here
    // This could involve updating the local state and/or making an API call
    console.log("Submitting changes:", changes);

    const updateData: UpdateListing = {
      title: changes.title,
      description: changes.description,
      rent: changes.rent,
      bond: changes.bond,
      billsIncluded: changes.billsIncluded,
      parking: changes.parking,
      bathroom: changes.bathroom,
      roomType: changes.roomType,
      furnished: changes.furnished,
      images: changes.images?.map((image) => image.mediaId),
      availableFrom: changes.availableFrom,
      minimumStay: changes.minimumStay,
      maximumStay: changes.maximumStay
    };

    const patchData: PatchApiListingsByIdData = {
      id: id,
      requestBody: updateData
    };

    try {
      await patchApiListingsById(patchData);
      // Update localListing with changes
      setLocalListing(prev => updateLocalListing(prev, changes));
    } catch (error) {
      alert("Could not submit edit changes. Try again later.")
    }
  };

  return (
    <>
      <MainHeader />

      {isLoading ? (
        <ListingSkeleton />
      ) : notFound ? (
        <div className="mx-auto sm:pt-6 max-w-7xl min-h-[60vh] flex items-center justify-center">
          <ListingNotFound />
        </div>
      ) : localListing && (
        <div className="mx-auto sm:pt-6 max-w-7xl">
          <ImageCarousel
            key={`carousel-${localListing.id}`}
            editButton={editMode && (
              <button className="bg-white p-2 rounded-full shadow-md absolute top-2 right-2 flex justify-center items-center w-12 h-12 z-10 text-[#2476f2] hover:text-blue-700" onClick={(event) => { event.preventDefault(); handleModalOpen(ModalPrompt.Images); }} aria-label="Zoom in">
                <EditIcon width="90%" height="90%" />
              </button>
            )}
            // Pass carousel images with small version for the first image if we came from a listing card
            images={prepareCarouselImages(localListing, cameFromListingCard)}
          />

          <div className="content-wrapper flex px-2 lg:px-0">
            <div className='lg:pl-1 lg:w-[60%]'>
              <div className='py-6'>
                <div className="price-container">
                  <h3 className="text-lg lg:text-xl font-medium text-gray-800">
                    {"$" + localListing.rent + " / week"}
                  </h3>

                  <h3 className="text-lg lg:text-xl font-light text-gray-400">
                    {localListing.billsIncluded ? " (Including bills)" : ""}
                  </h3>

                  {editMode && (<EditButton onClick={() => handleModalOpen(ModalPrompt.Details, 'Pricing')} modalPrompt={ModalPrompt.Details} />)}

                </div>

                <h3 className="text-base font-normal text-gray-400 pt-1">
                  Room for rent in {capitalizeFirstLetters(localListing.suburb)}, {localListing.state} {localListing.postcode}
                </h3>

              </div>

              <hr></hr>
              <div className="text-lg lg:text-xl font-medium pt-6 flex">
                Description
                {editMode && (<EditButton onClick={handleModalOpen} modalPrompt={ModalPrompt.Description} />)}
              </div>

              <p className="text-base text-gray-800 py-6">
                {formatDescription(localListing.description || '')}
              </p>

              <hr></hr>

              {localListing && <DetailsSection localListing={localListing} onModalClick={handleModalOpen} editMode={editMode} />}

              <hr></hr>

              <div className="block py-6 lg:hidden">
                <FloatingMessageBox
                  userName={localListing.owner?.name}
                  userId={localListing.owner.id}
                  profilePicture={localListing.owner?.profilePicture}
                  listingId={localListing.id}
                  ref={messageBoxRef}
                />
              </div>

              <div className="py-6" ref={mapRef}>
                {localListing.latitude != null && localListing.longitude != null && (
                  <PMTilesMap
                    key={`map-${localListing.id}`}
                    startingLocation={[
                      localListing!.latitude as number,
                      localListing!.longitude as number
                    ]} />
                )}
                <p className="text-sm text-gray-800 pt-2 italic">
                  For privacy the location shown is approximate within 200m. Please message the owner for the exact location.
                </p>
              </div>
            </div>

            <div className="hidden lg:block floating-box-container w-[38%] py-6 sticky top-20">
              <div
                className="sticky top-[84px]" // Adjust top value to match your header height
              >
                <FloatingMessageBox
                  userName={localListing.owner?.name}
                  userId={localListing.owner.id}
                  profilePicture={localListing.owner?.profilePicture}
                  listingId={localListing.id}
                />
              </div>
            </div>
          </div>

          <hr></hr>

          <OtherListingsSection key={`related-${localListing.id}`} localListing={localListing} />

          <ToastError error={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <Footer></Footer>

      {editMode &&
        (<EditModal
          open={modalOpen}
          onClose={handleModalClose}
          currentPrompt={currentModalPrompt}
          localListing={localListing}
          onSubmit={handleSubmit}
          initialDetailsTab={initialDetailsTab}
          initialImages={initialImages}
        />)
      }

      {!isLoading && !notFound && <MobileScrollButton messageBoxRef={messageBoxRef} />}
    </>
  );
}

export default function Listing() {
  return <ListingWrap />;
}